import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const NOTIFICATION_CHANNELS = ['SMS', 'EMAIL', 'BOTH'];
const EXPIRY_OPTIONS = [
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

export default function AdhocFormSender({ practiceId, onSent }) {
  const [questionCategories, setQuestionCategories] = useState([]);
  const [formCategories, setFormCategories] = useState([]);
  const [consentForms, setConsentForms] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [selectedQCats, setSelectedQCats] = useState([]);
  const [selectedFCats, setSelectedFCats] = useState([]);
  const [selectedFormIds, setSelectedFormIds] = useState([]);
  const [reason, setReason] = useState('');
  const [channel, setChannel] = useState('SMS');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [message, setMessage] = useState('');
  // outbound #254: optional notification templates ('' = built-in message)
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplateId, setSmsTemplateId] = useState('');
  const [emailTemplateId, setEmailTemplateId] = useState('');
  useEffect(() => {
    if (!practiceId) return;
    api.getNotificationTemplates(practiceId, 'sms').then(r => setSmsTemplates(r.data || [])).catch(() => setSmsTemplates([]));
    api.getNotificationTemplates(practiceId, 'email').then(r => setEmailTemplates(r.data || [])).catch(() => setEmailTemplates([]));
  }, [practiceId]);
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const [qRes, fRes, cfRes, pRes] = await Promise.all([
        api.getIntakeQuestionCategories(practiceId),
        api.getIntakeFormCategories(practiceId),
        api.getAllConsentForms(practiceId).catch(() => ({ data: [] })),
        api.getIntakePackages(practiceId).catch(() => ({ data: [] }))
      ]);
      setQuestionCategories(qRes.data || []);
      setFormCategories(fRes.data || []);
      setConsentForms(cfRes.data || []);
      setPackages((pRes.data || []).filter(p => !p.is_adhoc)); // Only show non-adhoc packages as templates
    } catch (err) { console.error(err); }
  }, [practiceId]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const searchPatients = useCallback(async (query) => {
    if (!query || query.length < 2) { setPatients([]); return; }
    try {
      const res = await api.getPatients(practiceId, { search: query, limit: 20 });
      setPatients(res.data || res.patients || []);
    } catch (err) { console.error(err); }
  }, [practiceId]);

  useEffect(() => {
    const timer = setTimeout(() => searchPatients(patientSearch), 300);
    return () => clearTimeout(timer);
  }, [patientSearch, searchPatients]);

  const handlePackageSelect = async (packageId) => {
    setSelectedPackage(packageId);
    if (!packageId) { setSelectedQCats([]); setSelectedFCats([]); return; }
    try {
      const res = await api.getIntakePackage(practiceId, packageId);
      const pkg = res.data;
      if (pkg) {
        setSelectedQCats((pkg.question_category_ids || []).map(id => parseInt(id)));
        setSelectedFCats((pkg.form_category_ids || []).map(id => parseInt(id)));
        if (!reason) setReason(pkg.package_name || '');
      }
    } catch { /* ignore */ }
  };

  const togglePatient = (patient) => {
    setSelectedPatients(prev => {
      const exists = prev.find(p => p.id === patient.id);
      if (exists) return prev.filter(p => p.id !== patient.id);
      return [...prev, patient];
    });
  };

  const toggleQCat = (id) => {
    setSelectedQCats(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleFCat = (id) => {
    setSelectedFCats(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleFormId = (id) => {
    setSelectedFormIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const send = async () => {
    if (selectedPatients.length === 0) return alert('Select at least one patient');
    if (selectedQCats.length === 0 && selectedFCats.length === 0 && selectedFormIds.length === 0) {
      return alert('Select at least one category or individual consent form');
    }
    if (!reason.trim()) return alert('Please enter a reason');

    setSending(true);
    try {
      await api.createIntakeAdhoc(practiceId, {
        patient_ids: selectedPatients.map(p => p.id),
        question_category_ids: selectedQCats,
        form_category_ids: selectedFCats,
        consent_form_ids: selectedFormIds,
        reason: reason.trim(),
        notification_channel: channel,
        expires_in_days: expiresInDays,
        message: message.trim() || undefined,
        sms_template_id: (channel === 'SMS' || channel === 'BOTH') && smsTemplateId ? Number(smsTemplateId) : undefined,
        email_template_id: (channel === 'EMAIL' || channel === 'BOTH') && emailTemplateId ? Number(emailTemplateId) : undefined
      });
      alert(`Forms sent to ${selectedPatients.length} patient(s)`);
      resetForm();
      if (onSent) onSent();
    } catch (err) {
      alert('Failed to send: ' + err.message);
    }
    setSending(false);
  };

  const resetForm = () => {
    setSelectedPatients([]);
    setSelectedQCats([]);
    setSelectedFCats([]);
    setSelectedFormIds([]);
    setReason('');
    setChannel('SMS');
    setExpiresInDays(7);
    setMessage('');
    setShowForm(false);
    setPatientSearch('');
    setPatients([]);
  };

  if (!showForm) {
    return (
      <div style={{ marginBottom: 16 }}>
        <button style={s.btnPrimary} onClick={() => setShowForm(true)}>+ Send Ad-Hoc Forms</button>
      </div>
    );
  }

  return (
    <div style={s.formCard}>
      <h4 style={s.formTitle}>Send Ad-Hoc Forms</h4>

      {/* Patient Search */}
      <div style={s.formRow}>
        <label style={s.label}>Patients</label>
        <input
          style={s.input}
          placeholder="Search patients by name or phone..."
          value={patientSearch}
          onChange={e => setPatientSearch(e.target.value)}
        />
        {patients.length > 0 && (
          <div style={s.dropdown}>
            {patients.map(p => {
              const selected = selectedPatients.find(sp => sp.id === p.id);
              return (
                <div key={p.id} style={{ ...s.dropdownItem, background: selected ? '#eef2ff' : '#fff' }}
                  onClick={() => togglePatient(p)}>
                  <span>{p.first_name} {p.last_name}</span>
                  <span style={s.dropdownMeta}>{p.phone || p.email || ''}</span>
                </div>
              );
            })}
          </div>
        )}
        {selectedPatients.length > 0 && (
          <div style={s.chipRow}>
            {selectedPatients.map(p => (
              <span key={p.id} style={s.chip}>
                {p.first_name} {p.last_name}
                <span style={s.chipRemove} onClick={() => togglePatient(p)}>&times;</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Package Template */}
      {packages.length > 0 && (
        <div style={s.formRow}>
          <label style={s.label}>Use Template (optional)</label>
          <select value={selectedPackage} onChange={e => handlePackageSelect(e.target.value)}
            style={{ ...s.input, maxWidth: 400 }}>
            <option value="">— Select a package template to pre-fill categories —</option>
            {packages.map(p => (
              <option key={p.id} value={p.id}>{p.package_name}</option>
            ))}
          </select>
          <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>
            Selecting a template pre-fills the categories below. You can still add or remove individual categories.
          </p>
        </div>
      )}

      {/* Question Categories */}
      <div style={s.formRow}>
        <label style={s.label}>Question Categories</label>
        <div style={s.checkboxGrid}>
          {questionCategories.map(cat => (
            <label key={cat.id} style={s.checkLabel}>
              <input type="checkbox" checked={selectedQCats.includes(cat.id)}
                onChange={() => toggleQCat(cat.id)} />
              {cat.category_name}
              <span style={s.badgeSmall}>{cat.category_type}</span>
            </label>
          ))}
          {questionCategories.length === 0 && <span style={s.muted}>No question categories configured</span>}
        </div>
      </div>

      {/* Form/Consent Categories */}
      <div style={s.formRow}>
        <label style={s.label}>Consent / Form Categories</label>
        <div style={s.checkboxGrid}>
          {formCategories.map(cat => (
            <label key={cat.id} style={s.checkLabel}>
              <input type="checkbox" checked={selectedFCats.includes(cat.id)}
                onChange={() => toggleFCat(cat.id)} />
              {cat.category_name}
              <span style={s.badgeSmall}>{cat.category_type}</span>
            </label>
          ))}
          {formCategories.length === 0 && <span style={s.muted}>No form categories configured</span>}
        </div>
      </div>

      {/* Individual Consent Forms — pick one or more specific forms instead of whole categories */}
      <div style={s.formRow}>
        <label style={s.label}>
          Individual Consent Forms
          <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>
            (optional — narrows the selected category to these specific forms)
          </span>
        </label>
        <div style={s.checkboxGrid}>
          {consentForms.map(f => (
            <label key={f.id} style={s.checkLabel}>
              <input type="checkbox" checked={selectedFormIds.includes(f.id)}
                onChange={() => toggleFormId(f.id)} />
              {f.form_name}
              <span style={s.badgeSmall}>{f.category_name}</span>
            </label>
          ))}
          {consentForms.length === 0 && <span style={s.muted}>No active consent forms configured</span>}
        </div>
      </div>

      {/* Options */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div style={s.formRow}>
          <label style={s.label}>Reason</label>
          <input style={s.input} placeholder="e.g. Expired consent, COVID screening"
            value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div style={s.formRow}>
          <label style={s.label}>Notification Channel</label>
          <select style={s.input} value={channel} onChange={e => setChannel(e.target.value)}>
            {NOTIFICATION_CHANNELS.map(ch => <option key={ch} value={ch}>{ch}</option>)}
          </select>
        </div>
        <div style={s.formRow}>
          <label style={s.label}>Expires In</label>
          <select style={s.input} value={expiresInDays} onChange={e => setExpiresInDays(Number(e.target.value))}>
            {EXPIRY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
      </div>

      {/* Notification templates — outbound #254 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {(channel === 'SMS' || channel === 'BOTH') && (
          <div style={s.formRow}>
            <label style={s.label}>SMS Template</label>
            <select style={s.input} value={smsTemplateId} onChange={e => setSmsTemplateId(e.target.value)}>
              <option value="">Default message (built-in)</option>
              {smsTemplates.map(t => <option key={t.id} value={t.id}>{t.template_name}</option>)}
            </select>
          </div>
        )}
        {(channel === 'EMAIL' || channel === 'BOTH') && (
          <div style={s.formRow}>
            <label style={s.label}>Email Template</label>
            <select style={s.input} value={emailTemplateId} onChange={e => setEmailTemplateId(e.target.value)}>
              <option value="">Default message (built-in)</option>
              {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.template_name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div style={{ ...s.muted, marginTop: -6, marginBottom: 10 }}>
        Templates are managed under Notifications → Templates. Variables for ad-hoc sends: {'{{adhoc_link}}'}, {'{{expires_date}}'}, {'{{reason}}'}, {'{{custom_message}}'}, {'{{patient_first_name}}'}, {'{{practice_name}}'}. The link is appended automatically if a template leaves it out.
      </div>

      {/* Custom Message */}
      <div style={s.formRow}>
        <label style={s.label}>Custom Message (optional)</label>
        <textarea style={{ ...s.input, height: 60, resize: 'vertical' }}
          placeholder="Optional message to include in the notification..."
          value={message} onChange={e => setMessage(e.target.value)} />
      </div>

      {/* Actions */}
      <div style={s.formActions}>
        <button style={s.btnPrimary} onClick={send} disabled={sending}>
          {sending ? 'Sending...' : `Send to ${selectedPatients.length} Patient(s)`}
        </button>
        <button style={s.btnSecondary} onClick={resetForm}>Cancel</button>
      </div>
    </div>
  );
}

const s = {
  formCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 },
  formTitle: { fontSize: 15, fontWeight: 600, color: '#111827', margin: '0 0 16px 0' },
  formRow: { marginBottom: 12 },
  formActions: { display: 'flex', gap: 8, marginTop: 16 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, outline: 'none', boxSizing: 'border-box' },
  btnPrimary: { padding: '8px 16px', fontSize: 13, background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 },
  btnSecondary: { padding: '8px 16px', fontSize: 13, background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' },
  checkboxGrid: { display: 'flex', flexWrap: 'wrap', gap: '6px 16px' },
  checkLabel: { fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 4 },
  badgeSmall: { fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#f3f4f6', color: '#6b7280', marginLeft: 2 },
  dropdown: { border: '1px solid #e5e7eb', borderRadius: 6, marginTop: 4, maxHeight: 160, overflowY: 'auto', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  dropdownItem: { padding: '6px 12px', fontSize: 13, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f9fafb' },
  dropdownMeta: { fontSize: 11, color: '#9ca3af' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '2px 8px', borderRadius: 12, background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' },
  chipRemove: { cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: 1, color: '#6366f1' },
  muted: { fontSize: 12, color: '#9ca3af' },
};
